import type { Styles } from '@adornis/ass/style.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import type { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import type { ChemistryControllerHost } from '@adornis/chemistry/controllers/chemistry-controller.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { ColorType } from '@adornis/chemistry/elements/theming/coloring-system.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';
import type { Bundle } from './entities/Bundle.js';
import { Product } from './entities/products.js';
import type { UserStammdaten } from './entities/stammdaten.js';
import type { User } from './entities/user.js';
import { Workshop } from './entities/workshop.js';
import { ParticipationState } from './enums/enums.js';
import type { PaymentPlugin } from './plugins/plugin-types.js';
import { getPluginForScope } from './plugins/plugin.js';

export function productPriceNumber(
  workshop: NarrowedEntity<Product, { hasPrice: 1; prices: { unitAmount: 1 } }>,
): number | [number, number] {
  if (!workshop.hasPrice) return 0;
  const prices = workshop.prices
    .filter(price => price._class === 'Price')
    .map(p => p.unitAmount)
    .toSorted((a, b) => a - b);
  if (prices.length === 0) throw new Error('unreachable');
  else if (prices.length === 1 || prices.at(0) === prices.at(-1)) return prices[0] as number;
  else return [prices.at(0) as number, prices.at(-1) as number];
}

export function getTargetPrice(
  targetProd: Product,
  curUser: User,
  stammdaten: UserStammdaten,
  payPlugin: PaymentPlugin<string, string>,
) {
  return payPlugin.getProductPrice(targetProd, curUser, stammdaten)?.unitAmount ?? 0;
}

export function productPrice(workshop: Product) {
  const price = productPriceNumber(workshop);
  return formatPrice(price);
}

// price can also be price range
export function formatPrice(amount: number | [number, number] | null, isEmptyIndicator = '---') {
  if (amount === null) return isEmptyIndicator;

  const _formatPriceRaw = amount => (amount / 100).toFixed(2).replace(/,/g, '').replace('.', ',');
  const _formatPrice = amount =>
    Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount / 100);

  if (typeof amount === 'number') {
    if (amount < 0) throw new Error('UnitPrice is negative');
    return _formatPrice(amount);
  }

  const [amount1, amount2] = amount;
  if (amount1 < 0) throw new Error('UnitPrice is negative (amount1)');
  if (amount2 < 0) throw new Error('UnitPrice is negative (amount2)');
  if (amount1 === amount2) throw new Error('pricerange is actually only single value');
  if (amount1 > amount2) throw new Error(`range is invalid: ${amount1} - ${amount2}`);
  return _formatPriceRaw(amount1) + ' - ' + _formatPrice(amount2);
}

export const dataviewEmptyState = stateful((props: { styles?: Styles; text?: string }) => {
  const { styles, text } = props;
  const design = useObservable(() => DesignSystem.currentTheme, []);
  return html`
    <tr-flex
      w100
      padding="sm"
      center
      crossaxis-center
      ${acss({ background: design?.colors.tone.onyx, color: '#fff', ...styles })}
    >
      <tr-text> ${text ?? 'Keine Datensätze gefunden'} </tr-text>
    </tr-flex>
  `;
});

export const dataviewLoadingState = (host: ChemistryLitElement) => html`
  <tr-flex w100 padding="sm" center crossaxis-center ${css({ background: host.colors.tone.onyx, color: '#fff' })}>
    <tr-text> Datensätze werden geladen... </tr-text>
  </tr-flex>
`;

export function getParticipationStateColors(state: ParticipationState, host: ChemistryControllerHost) {
  return (
    {
      [ParticipationState.PARTICIPATED]: host.colors.tone.success,
      [ParticipationState.IN]: host.colors.tone.light_blue,
      [ParticipationState.LEFT]: host.colors.tone.light_orange,
      [ParticipationState.WAITING]: host.colors.tone.light_bronze,
      [ParticipationState.SUSPENDED]: host.colors.tone.light_grau,
    } as { [k in ParticipationState]: ColorType }
  )[state];
}

export type BookingBillDataBundle<BundleType extends NarrowedEntity<Bundle, {}>> = {
  bundle: BundleType;
  products: Product[];
};
export type BookingBillData<BundleType extends NarrowedEntity<Bundle, {}>> = {
  booked: (Product | BookingBillDataBundle<BundleType>)[];
  totalPrice: 0;
};

export function getProductsBestBundled<T extends NarrowedEntity<Bundle, { productIDs: 1 }>>(
  selectedProducts: Product[],
  selectedBundles: T[],
) {
  const bundles: NarrowedEntity<Bundle, { productIDs: 1 }>[] = selectedBundles;
  let singleSelectedProds = [...selectedProducts];
  let prodsGroupByBundle = new Array<BookingBillDataBundle<T>>();

  bundles.forEach(curBundle => {
    const bundleProds = new Array<Product>();
    const leftProds = new Array<Product>();

    singleSelectedProds.forEach(curProd => {
      if (curBundle.productIDs.includes(curProd._id)) bundleProds.push(curProd);
      else leftProds.push(curProd);
    });

    if (bundleProds.length === curBundle.productIDs.length) {
      singleSelectedProds = leftProds;
      prodsGroupByBundle.push({
        bundle: curBundle,
        products: bundleProds,
      });
    }
  });

  return [...prodsGroupByBundle, ...singleSelectedProds];
}

export async function getBillData<
  T extends NarrowedEntity<Bundle, { productIDs: 1; hasPrice: 1; price: { unitAmount: 1 } }>,
>(selectedProducts: Product[], selectedBundles: T[], userID: string) {
  const bestFittedBooks = getProductsBestBundled(selectedProducts, selectedBundles);
  let totalPrice = 0;
  for (const curBook of bestFittedBooks) {
    if (curBook instanceof Product) {
      if (curBook instanceof Workshop && curBook.leftCapacity === 0) void 0; // ignore waitinglist workshops
      else {
        const paymentPlugin = getPluginForScope('payment', curBook.institutionID);
        const price = await paymentPlugin.getProductPrice(curBook._id, userID);
        totalPrice += price?.unitAmount ?? 0;
      }
    } else {
      const curBundle = curBook.bundle;
      if (curBundle.hasPrice) totalPrice += curBundle.price.unitAmount;
    }
  }

  return {
    booked: bestFittedBooks,
    totalPrice,
  };
}
